import React, { useState, useLayoutEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  AUGMENT_HELP_POPOVER_Z_INDEX,
  computeCenteredPopoverDock,
  getMobileBottomNavReserve,
  getTopNavReserve
} from '../utils/computeCenteredPopoverDock';
import './AugmentHelpPopover.css';

/**
 * Opens augment help from the ? icon (click only). Same pattern as MatchLeagueTablePopover:
 * portaled centered panel, backdrop or panel tap closes.
 */
const AugmentHelpPopover = ({ title, triggerAriaLabel, dialogAriaLabel, children }) => {
  const [open, setOpen] = useState(false);
  const [dockStyle, setDockStyle] = useState(null);
  const anchorRef = useRef(null);

  const updateDockPosition = useCallback(() => {
    if (!open || !anchorRef.current) return;
    const anchorEl = anchorRef.current;
    const main = anchorEl.closest('.main-content');
    const col = main?.getBoundingClientRect();
    setDockStyle(
      computeCenteredPopoverDock({
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
        mainContentRect: col ? { left: col.left, width: col.width } : null,
        topNavHeight: getTopNavReserve(),
        bottomNavHeight: getMobileBottomNavReserve()
      })
    );
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setDockStyle(null);
      return undefined;
    }
    updateDockPosition();
    window.addEventListener('scroll', updateDockPosition, true);
    window.addEventListener('resize', updateDockPosition);
    return () => {
      window.removeEventListener('scroll', updateDockPosition, true);
      window.removeEventListener('resize', updateDockPosition);
    };
  }, [open, updateDockPosition]);

  const closePanel = () => setOpen(false);

  const portalContent = open && dockStyle && (
    <>
      <button
        type="button"
        className="augment-help-popover__backdrop"
        aria-label={`Close ${title}`}
        onClick={closePanel}
      />
      <div
        className="augment-help-popover augment-help-popover--docked"
        style={{
          position: 'fixed',
          left: dockStyle.left,
          width: dockStyle.width,
          top: dockStyle.top,
          maxHeight: dockStyle.maxHeight,
          transform: dockStyle.transform,
          zIndex: AUGMENT_HELP_POPOVER_Z_INDEX
        }}
        role="dialog"
        aria-label={dialogAriaLabel || title}
        onClick={closePanel}
      >
        <div className="augment-help-popover__head">
          <div className="augment-help-popover__head-main">
            <span className="augment-help-popover__title">{title}</span>
          </div>
          <button
            type="button"
            className="augment-help-popover__close"
            aria-label={`Close ${title}`}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              closePanel();
            }}
          >
            ×
          </button>
        </div>
        <div className="augment-help-popover__body">{children}</div>
      </div>
    </>
  );

  return (
    <>
      <div ref={anchorRef} className="augment-help-popover-anchor">
        <button
          type="button"
          className="augment-help-popover-trigger"
          aria-label={triggerAriaLabel}
          aria-expanded={open}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setOpen((o) => !o);
          }}
        >
          ?
        </button>
      </div>
      {portalContent && typeof document !== 'undefined' && document.body
        ? ReactDOM.createPortal(portalContent, document.body)
        : null}
    </>
  );
};

export default AugmentHelpPopover;
